import type { ServerMessage } from '@jianghu/protocol';
import { wsProtocols, wsUrl } from '../../services/backend';

export type NetStatus = 'connecting' | 'online' | 'reconnecting' | 'offline';

const FATAL_KICKS = new Set([
  'duplicate_login',
  'logged_out',
  'character_not_found',
  'invalid_character',
  'protocol_violation',
]);

/**
 * WebSocket transport with heartbeat/RTT measurement and automatic reconnection with backoff.
 * The session cookie authenticates the upgrade; when a browser withholds the cross-site cookie,
 * the session token is offered as a WebSocket subprotocol (see services/backend.ts).
 */
export class NetClient {
  private ws: WebSocket | null = null;
  private attempts = 0;
  private pingTimer: number | null = null;
  private reconnectTimer: number | null = null;
  private closedByUser = false;
  private kicked: string | null = null;
  rtt = 0;
  bytesIn = 0;
  messagesIn = 0;

  constructor(
    private readonly characterId: string,
    private readonly onMessage: (m: ServerMessage) => void,
    private readonly onStatus: (s: NetStatus, detail?: string) => void,
  ) {}

  connect(): void {
    this.closedByUser = false;
    this.open(false);
  }

  private open(isRetry: boolean): void {
    this.onStatus(isRetry ? 'reconnecting' : 'connecting');
    const ws = new WebSocket(wsUrl(this.characterId), wsProtocols());
    this.ws = ws;
    ws.onmessage = (ev) => {
      const text = typeof ev.data === 'string' ? ev.data : '';
      this.bytesIn += text.length;
      this.messagesIn++;
      let msg: ServerMessage;
      try {
        msg = JSON.parse(text) as ServerMessage;
      } catch {
        return;
      }
      if (msg.t === 'welcome') {
        this.attempts = 0;
        this.onStatus('online');
      } else if (msg.t === 'pong') {
        this.rtt = performance.now() - msg.c;
      } else if (msg.t === 'kick') {
        this.kicked = msg.reason;
      }
      this.onMessage(msg);
    };
    ws.onopen = () => {
      this.startPing();
    };
    ws.onclose = () => {
      this.stopPing();
      if (this.ws !== ws) return;
      this.ws = null;
      if (this.closedByUser) return;
      if (
        this.kicked &&
        (FATAL_KICKS.has(this.kicked) || (this.kicked === 'server_shutdown' && this.attempts > 8))
      ) {
        this.onStatus('offline', this.kicked);
        return;
      }
      this.scheduleReconnect();
    };
    ws.onerror = () => {};
  }

  private scheduleReconnect(): void {
    this.attempts++;
    if (this.attempts > 10) {
      this.onStatus('offline', 'connection_lost');
      return;
    }
    const delay = Math.min(10_000, 400 * 2 ** (this.attempts - 1)) * (0.8 + Math.random() * 0.4);
    this.onStatus('reconnecting');
    this.reconnectTimer = window.setTimeout(() => {
      this.kicked = null;
      this.open(true);
    }, delay);
  }

  private startPing(): void {
    this.stopPing();
    const ping = (): void => this.send({ t: 'ping', c: performance.now() });
    ping();
    this.pingTimer = window.setInterval(ping, 2000);
  }

  private stopPing(): void {
    if (this.pingTimer !== null) window.clearInterval(this.pingTimer);
    this.pingTimer = null;
  }

  get open_(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  send(msg: object): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }

  close(): void {
    this.closedByUser = true;
    this.stopPing();
    if (this.reconnectTimer !== null) window.clearTimeout(this.reconnectTimer);
    this.ws?.close(1000, 'leaving');
    this.ws = null;
  }
}
